var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import moment from 'moment';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import SendIcon from 'images/v5/icons/arrow-up.svg';
import CopyIcon from 'images/v5/icons/copy-01.svg';
import PlusIcon from 'images/v5/icons/plus.svg';
import RetryIcon from 'images/v5/icons/refresh-ccw-01.svg';
import CloseIcon from 'images/v5/icons/x-close.svg';
import Button from 'layout/atoms/Button/Button';
import styles from './HelpAgent.module.scss';
import { agentAvailable, HelpAgentError, streamAgent, } from './helpAgentApi';
const CloseAgentContext = createContext(() => undefined);
function AgentLink({ href, children }) {
    const close = useContext(CloseAgentContext);
    if (!href)
        return React.createElement("span", null, children);
    return href.startsWith('/help/article/') ? (React.createElement(Link, { to: href, onClick: close }, children)) : (React.createElement("a", { href: href }, children));
}
function HelpAgent({ open, onClose, language, }) {
    const { t } = useTranslation('helpCenter');
    const [turns, setTurns] = useState([]);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(null);
    const [statusAttempt, setStatusAttempt] = useState(0);
    const [startedAt, setStartedAt] = useState(null);
    const active = useRef(null);
    const input = useRef(null);
    const panel = useRef(null);
    const log = useRef(null);
    const follow = useRef(true);
    const nextId = useRef(0);
    useEffect(() => () => { var _a; return (_a = active.current) === null || _a === void 0 ? void 0 : _a.abort(); }, []);
    useEffect(() => {
        if (!open)
            return undefined;
        const controller = new AbortController();
        let disposed = false;
        const timer = window.setTimeout(() => controller.abort(), 10000);
        setStatus('loading');
        agentAvailable(controller.signal)
            .then((available) => {
            if (disposed)
                return;
            setStatus(available ? 'ready' : 'unavailable');
            setError((current) => {
                if (!available)
                    return 'NOT_CONFIGURED';
                return ['NOT_CONFIGURED', 'UNAVAILABLE', 'UNAUTHORIZED'].includes(current) ? '' : current;
            });
        })
            .catch((reason) => {
            if (disposed)
                return;
            setStatus('unavailable');
            setError(reason instanceof HelpAgentError ? reason.code : 'UNAVAILABLE');
        })
            .finally(() => window.clearTimeout(timer));
        return () => {
            disposed = true;
            controller.abort();
            window.clearTimeout(timer);
        };
    }, [open, statusAttempt]);
    useEffect(() => {
        if (follow.current && log.current)
            log.current.scrollTop = log.current.scrollHeight;
    }, [turns, busy, error, open]);
    // The panel is not modal, so move focus to the message field ourselves once it can be used
    useEffect(() => {
        var _a;
        if (open && status === 'ready')
            (_a = input.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [open, status]);
    // Escape closes the agent while focus is inside the panel
    useEffect(() => {
        const element = panel.current;
        if (!open || !element)
            return undefined;
        const handleKeyDown = (event) => {
            var _a;
            if (event.key !== 'Escape')
                return;
            (_a = active.current) === null || _a === void 0 ? void 0 : _a.abort();
            onClose();
        };
        element.addEventListener('keydown', handleKeyDown);
        return () => element.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);
    useEffect(() => {
        if (copied === null)
            return undefined;
        const timer = window.setTimeout(() => setCopied(null), 2000);
        return () => window.clearTimeout(timer);
    }, [copied]);
    const stop = () => {
        var _a;
        (_a = active.current) === null || _a === void 0 ? void 0 : _a.abort();
    };
    const close = () => {
        stop();
        onClose();
    };
    const send = (question_1, ...args_1) => __awaiter(this, [question_1, ...args_1], void 0, function* (question, retry = false) {
        var _a;
        const text = question.trim();
        if (!text || text.length > 2000 || active.current || status !== 'ready')
            return;
        const previous = retry ? turns.slice(0, -1) : turns;
        const history = previous
            .filter((turn) => turn.complete)
            .flatMap((turn) => [
            { role: 'user', content: turn.question },
            { role: 'assistant', content: turn.answer },
        ]);
        const id = nextId.current;
        nextId.current += 1;
        const controller = new AbortController();
        active.current = controller;
        let timedOut = false;
        const timer = window.setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, 190000);
        setTurns([...previous, { id, question: text, answer: '', sources: [], complete: false }]);
        setStartedAt((current) => current !== null && current !== void 0 ? current : Date.now());
        setDraft('');
        setError('');
        setBusy(true);
        follow.current = true;
        try {
            yield streamAgent(language, [...history, { role: 'user', content: text }], controller.signal, (event) => {
                setTurns((current) => current.map((turn) => {
                    if (turn.id !== id)
                        return turn;
                    if (event.type === 'delta')
                        return Object.assign(Object.assign({}, turn), { answer: turn.answer + event.content });
                    if (event.type === 'sources')
                        return Object.assign(Object.assign({}, turn), { sources: event.sources });
                    return Object.assign(Object.assign({}, turn), { complete: true });
                }));
            });
        }
        catch (reason) {
            if (controller.signal.aborted)
                setError(timedOut ? 'TIMEOUT' : 'STOPPED');
            else
                setError(reason instanceof HelpAgentError ? reason.code : 'UNAVAILABLE');
        }
        finally {
            window.clearTimeout(timer);
            active.current = null;
            setBusy(false);
            (_a = input.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    });
    const newConversation = () => {
        var _a;
        setTurns([]);
        setStartedAt(null);
        setError('');
        setCopied(null);
        setDraft('');
        (_a = input.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const copy = (turn) => __awaiter(this, void 0, void 0, function* () {
        try {
            yield navigator.clipboard.writeText(turn.answer);
            setCopied(turn.id);
        }
        catch (_a) {
            setError('COPY_FAILED');
        }
    });
    const errorText = error
        ? t(`AGENT_ERROR_${error}`, { defaultValue: t('AGENT_ERROR_UNAVAILABLE') })
        : '';
    const lastTurn = turns[turns.length - 1];
    const startedLabel = startedAt
        ? moment(startedAt).format(moment(startedAt).isSame(moment(), 'day')
            ? `[${t('AGENT_TODAY')}] HH:mm`
            : 'DD.MM.YYYY HH:mm')
        : '';
    if (!open)
        return null;
    return (React.createElement("section", { role: "dialog", "aria-modal": "false", "aria-labelledby": "help-agent-title", className: styles.panel, ref: panel },
        React.createElement("header", { className: styles.header },
            React.createElement("div", null,
                React.createElement("h2", { id: "help-agent-title", className: styles.title },
                    t('AGENT_TITLE'),
                    React.createElement("span", { className: styles.badge }, t('AGENT_BETA'))),
                React.createElement("p", { className: styles.subtitle }, t('AGENT_SUBTITLE'))),
            React.createElement("div", { className: styles.tools },
                React.createElement(Button, { size: "s", theme: "ghost", shape: "rounded", title: t('AGENT_NEW'), "aria-label": t('AGENT_NEW'), onClick: newConversation, disabled: busy || !turns.length, leftIcon: React.createElement(PlusIcon, null) }),
                React.createElement(Button, { size: "s", theme: "ghost", shape: "rounded", leftIcon: React.createElement(CloseIcon, null), title: t('AGENT_CLOSE'), "aria-label": t('AGENT_CLOSE'), onClick: close }))),
        React.createElement("div", { ref: log, className: styles.messages, role: "log", "aria-label": t('AGENT_CONVERSATION'), "aria-live": "polite", "aria-busy": busy, onScroll: () => {
                if (log.current)
                    follow.current =
                        log.current.scrollHeight - log.current.scrollTop - log.current.clientHeight < 80;
            } },
            !turns.length && React.createElement("p", { className: styles.welcome }, t('AGENT_WELCOME')),
            startedLabel && React.createElement("p", { className: styles.dateDivider }, startedLabel),
            turns.map((turn) => (React.createElement("section", { key: turn.id, className: styles.turn },
                React.createElement("div", { className: styles.question },
                    React.createElement("p", null, turn.question)),
                React.createElement("div", { className: styles.answer },
                    React.createElement(CloseAgentContext.Provider, { value: close },
                        React.createElement(ReactMarkdown, { remarkPlugins: [remarkGfm], skipHtml: true, disallowedElements: ['img'], urlTransform: (url) => url === 'mailto:support@fashionapps.net' ||
                                turn.sources.some((source) => source.url === url)
                                ? url
                                : '', components: { a: AgentLink } }, turn.answer)),
                    !turn.answer && busy && turn.id === lastTurn.id && (React.createElement("p", { className: styles.pending }, t('AGENT_THINKING'))),
                    !turn.complete && !(busy && turn.id === lastTurn.id) && (React.createElement("p", { className: styles.pending }, t('AGENT_INCOMPLETE'))),
                    turn.complete && (React.createElement("button", { type: "button", className: styles.iconButton, title: copied === turn.id ? t('AGENT_COPIED') : t('AGENT_COPY'), "aria-label": copied === turn.id ? t('AGENT_COPIED') : t('AGENT_COPY'), onClick: () => copy(turn) },
                        React.createElement(CopyIcon, null))))))),
            status === 'loading' && React.createElement("p", { className: styles.pending }, t('AGENT_CONNECTING')),
            error && (React.createElement("div", { className: styles.error, role: "alert" },
                React.createElement("p", null, errorText),
                !busy && status === 'ready' && lastTurn && !lastTurn.complete && (React.createElement("button", { type: "button", className: styles.retry, onClick: () => send(lastTurn.question, true) },
                    React.createElement(RetryIcon, null),
                    t('AGENT_RETRY'))),
                status === 'unavailable' && (React.createElement("button", { type: "button", className: styles.retry, onClick: () => setStatusAttempt((value) => value + 1) },
                    React.createElement(RetryIcon, null),
                    t('AGENT_RETRY'))),
                error !== 'STOPPED' && error !== 'COPY_FAILED' && (React.createElement("a", { href: "mailto:support@fashionapps.net" }, "support@fashionapps.net"))))),
        React.createElement("form", { className: styles.composer, onSubmit: (event) => {
                event.preventDefault();
                send(draft);
            } },
            React.createElement("div", { className: styles.composerField },
                React.createElement("label", { htmlFor: "help-agent-message", className: styles.srOnly }, t('AGENT_INPUT')),
                React.createElement("textarea", { id: "help-agent-message", ref: input, value: draft, onChange: (event) => setDraft(event.target.value), maxLength: 2000, rows: 3, placeholder: t('AGENT_INPUT'), disabled: status !== 'ready', onKeyDown: (event) => {
                        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                            event.preventDefault();
                            send(draft);
                        }
                    } }),
                React.createElement("div", { className: styles.composerBottom },
                    React.createElement("span", { className: styles.counter },
                        draft.length,
                        "/2000"),
                    busy ? (React.createElement("button", { type: "button", className: `${styles.iconButton} ${styles.send}`, title: t('AGENT_STOP'), "aria-label": t('AGENT_STOP'), onClick: stop },
                        React.createElement("span", { className: styles.stop }))) : (React.createElement("button", { type: "submit", className: `${styles.iconButton} ${styles.send}`, title: t('AGENT_SEND'), "aria-label": t('AGENT_SEND'), disabled: !draft.trim() || status !== 'ready' },
                        React.createElement(SendIcon, null)))))),
        React.createElement("footer", { className: styles.footer }, t('AGENT_NOTICE'))));
}
export default HelpAgent;
